import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime, pName, pMatch } from "@/lib/format";
import { FileText, Upload, Eye, Plus, Trash2, CheckCircle2, XCircle, ExternalLink, Truck } from "lucide-react";
import { MissedSaleDialog } from "@/components/MissedSaleDialog";
import { toast } from "sonner";
import { ProductCombobox } from "@/components/ProductCombobox";
import { RxDeliveryFields } from "@/components/RxDeliveryFields";

const LINE_REASONS = ["Not covered by insurance", "Out of stock", "Drug interaction / contraindication", "Illegible or unclear",
  "Dose or quantity query with prescriber", "Patient declined", "Controlled drug — original prescription required", "Prescription expired"];

const REJECT_REASONS = ["Out of stock", "Invalid prescription"];

export default function Prescriptions() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const { branches } = useBranch();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  // upload dialog
  const [open, setOpen] = useState(false);
  const blankForm = { patient_name: "", notes: "" };
  const blankDelivery = { fulfillment: "pickup", delivery_address: "", delivery_phone: "", delivery_scheduled_for: "", delivery_fee: "" };
  const [form, setForm] = useState(blankForm);
  const [dlv, setDlv] = useState(blankDelivery);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  // review/verify dialog
  const [review, setReview] = useState(null);
  const [imgUrl, setImgUrl] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [fileKind, setFileKind] = useState("other");
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [missedOpen, setMissedOpen] = useState(false);
  const [stockMap, setStockMap] = useState({});
  const [custMode, setCustMode] = useState("existing");
  const [customerId, setCustomerId] = useState("");
  const [schemes, setSchemes] = useState([]);
  const [newCust, setNewCust] = useState({ type: "cash", name: "", phone: "", insurance_scheme_id: "", scheme_member_no: "" });
  const [items, setItems] = useState([]);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [refillMonths, setRefillMonths] = useState(1);
  const [refillDays, setRefillDays] = useState(30);
  const today = new Date().toISOString().slice(0, 10);
  const prevPending = useRef(null);

  const load = () => api.get("/prescriptions", { params: { status: filter === "all" ? undefined : filter } }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  // new-prescription alert
  useEffect(() => {
    const check = () => api.get("/prescriptions", { params: { status: "pending" } }).then((r) => {
      const n = r.data.length;
      if (prevPending.current !== null && n > prevPending.current) {
        toast.info(`New prescription received — ${n} pending verification`);
        if (filter === "pending") setRows(r.data);
      }
      prevPending.current = n;
    }).catch(() => {});
    check();
    const iv = setInterval(check, 15000);
    return () => clearInterval(iv);
  }, [filter]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/insurance-schemes").then((r) => setSchemes(r.data)).catch(() => {});
  }, []);

  const loadStock = (bid) => {
    if (!bid) return setStockMap({});
    api.get("/stock", { params: { branch_id: bid } }).then((r) => {
      const m = {}; r.data.forEach((s) => { m[s.product_id] = s.total_qty; }); setStockMap(m);
    }).catch(() => setStockMap({}));
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a prescription image");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await api.post("/prescriptions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Prescription uploaded"); setOpen(false); setFile(null); setForm(blankForm); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const view = async (rx) => {
    try {
      const { data } = await api.get(`/files/${rx.storage_path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  const openReview = async (rx) => {
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setReview(rx); setImgUrl(""); setFileUrl(""); setFileKind("other"); setItems([{ product_id: "", quantity: 1 }]);
    setDlv({ ...blankDelivery, ...Object.fromEntries(Object.keys(blankDelivery).filter((k) => rx[k]).map((k) => [k, rx[k]])) });
    setCustMode(rx.customer_id ? "existing" : "existing"); setCustomerId(rx.customer_id || "");
    setNewCust({ type: "cash", name: rx.patient_name || "", phone: "", insurance_scheme_id: "", scheme_member_no: "" });
    setRejecting(false); setRejectReason(""); setRejectNote(""); setRefillMonths(1); setRefillDays(30);
    const bid = branches[0]?.id || ""; setBranchId(bid); loadStock(bid);
    try {
      const { data } = await api.get(`/files/${rx.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(data); setFileUrl(url);
      const sp = rx.storage_path || "";
      const ct = rx.content_type || data.type || "";
      if (ct.startsWith("image/") || /\.(png|jpe?g|gif|webp|heic)$/i.test(sp)) { setFileKind("image"); setImgUrl(url); }
      else if (ct === "application/pdf" || /\.pdf$/i.test(sp)) setFileKind("pdf");
      else setFileKind("other");
    } catch (e) { toast.error("Could not load the prescription file — " + apiError(e)); }
  };
  const closeReview = () => { if (fileUrl) URL.revokeObjectURL(fileUrl); setReview(null); setImgUrl(""); setFileUrl(""); setFileKind("other"); };

  const availFor = (pid) => stockMap[pid] ?? 0;
  const setItem = (idx, patch) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const anyStockIssue = items.some((it) => it.product_id && availFor(it.product_id) < Number(it.quantity || 0));

  const doVerify = async () => {
    const valid = items.filter((it) => it.product_id && Number(it.quantity) > 0);
    if (!valid.length) return toast.error("Add at least one medication");
    if (!valid.some((it) => !it.rejected)) return toast.error("Every line is rejected — use Reject for the whole prescription instead");
    if (valid.some((it) => it.rejected && !(it.reject_reason || "").trim())) return toast.error("Give a reason for each rejected line");
    setSaving(true);
    try {
      let cid = custMode === "existing" ? customerId : null;
      if (custMode === "new") {
        if (!newCust.name.trim()) { setSaving(false); return toast.error("Enter the customer name"); }
        if (newCust.type === "insurance" && !newCust.insurance_scheme_id) { setSaving(false); return toast.error("Select the insurance scheme"); }
        const { data } = await api.post("/customers", {
          type: newCust.type, name: newCust.name, phone: newCust.phone,
          insurance_scheme_id: newCust.insurance_scheme_id || null, scheme_member_no: newCust.scheme_member_no,
        });
        cid = data.id;
      }
      if (dlv.fulfillment === "delivery" && !dlv.delivery_address.trim()) { setSaving(false); return toast.error("Enter the delivery address"); }
      if (dlv.fulfillment === "delivery" && Number(dlv.delivery_fee) < 0) { setSaving(false); return toast.error("Delivery fee can't be negative"); }
      await api.post(`/prescriptions/${review.id}/verify`, {
        status: "verified", customer_id: cid || null, ...dlv, delivery_fee: Number(dlv.delivery_fee) || 0,
        months_authorised: Number(refillMonths) || 1, days_per_supply: Number(refillDays) || 30,
        items: valid.map((it) => ({ product_id: it.product_id, quantity: Number(it.quantity),
          generic_name: pName(products.find((p) => p.id === it.product_id)),
          ...(it.rejected ? { rejected: true, reject_reason: (it.reject_reason || "").trim() } : {}) })),
      });
      toast.success("Prescription verified"); closeReview(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const doReject = async () => {
    if (!rejectReason) return toast.error("Select a rejection reason");
    setSaving(true);
    try {
      const payload = { status: "rejected", reason: rejectReason, note: rejectNote };
      if (rejectReason === "Out of stock") {
        payload.branch_id = branchId;
        payload.items = items.filter((it) => it.product_id && Number(it.quantity) > 0).map((it) => ({ product_id: it.product_id, quantity: Number(it.quantity) }));
      }
      const { data } = await api.post(`/prescriptions/${review.id}/verify`, payload);
      if (data.reorder?.created?.length) toast.success(`Rejected — ${data.reorder.created.length} reorder PO(s) drafted`);
      else toast.success("Prescription rejected");
      closeReview(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Prescriptions" subtitle="Telemedicine & customer uploads — review, match stock and verify before processing"
        actions={can("prescriptions.upload") && <Button data-testid="upload-rx-btn" onClick={() => setOpen(true)}><Upload className="h-4 w-4 mr-1.5" /> Upload prescription</Button>} />
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="rx-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="verified" data-testid="rx-tab-verified">Verified</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="rx-tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="rx-tab-all">All</TabsTrigger>
        </TabsList>
        <TabsContent value={filter} className="mt-4">
          <Card className="shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                <tr><th className="py-2.5 px-3">Patient</th><th className="py-2.5 px-3">Source</th><th className="py-2.5 px-3">File</th><th className="py-2.5 px-3">Uploaded</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3">Reason</th><th className="py-2.5 px-3"></th></tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />No prescriptions</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/50">
                    <td className="py-2 px-3 font-medium text-foreground">{r.patient_name || r.customer_name || "—"}{r.fulfillment === "delivery" && <div className="text-[11px] font-normal text-muted-foreground flex items-center gap-1" data-testid={`rx-row-delivery-${r.id}`}><Truck className="h-3 w-3" /> Delivery · {r.delivery_address}</div>}</td>
                    <td className="py-2 px-3 text-xs capitalize text-muted-foreground">{r.source}</td>
                    <td className="py-2 px-3"><Button size="sm" variant="ghost" data-testid={`view-rx-${r.id}`} onClick={() => view(r)}><Eye className="h-3.5 w-3.5 mr-1" />view</Button></td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{fmtDateTime(r.created_at)}</td>
                    <td className="py-2 px-3"><StatusBadge value={r.status === "verified" || r.status === "completed" ? "ok" : r.status === "rejected" ? "out" : "pending"} label={r.status} /></td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">
                      {r.status === "rejected" ? (r.reject_reason || "—") : (r.items?.length ? `${r.items.filter((i) => !i.rejected).length} item(s)${r.items.some((i) => i.rejected) ? ` · ${r.items.filter((i) => i.rejected).length} rejected` : ""}` : "—")}
                      {r.refills_total > 0 && (r.status === "verified" || r.status === "completed") && (
                        <div className="mt-0.5" data-testid={`rx-refill-${r.id}`}>
                          <span className="font-medium text-foreground">{r.refills_remaining ?? r.refills_total}/{r.refills_total}</span> refills left
                          {r.next_refill_due && r.refills_remaining > 0 && <span className={`ml-1 ${r.next_refill_due <= today ? "text-rose-600 font-semibold" : ""}`}>· due {r.next_refill_due}</span>}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {can("prescriptions.verify") && r.status === "pending" && <Button size="sm" data-testid={`review-rx-${r.id}`} onClick={() => openReview(r)}>Review & verify</Button>}
                      {can("pos.sell") && r.status === "verified" && <Button size="sm" variant="outline" data-testid={`send-pos-${r.id}`} onClick={() => navigate(`/pos?rx=${r.id}`)}>{r.times_dispensed > 0 ? "Dispense refill" : "Send to POS"}</Button>}
                      {r.status === "completed" && <span className="text-xs text-emerald-600 font-medium">Completed</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Upload prescription</DialogTitle><DialogDescription>Attach a photo or PDF of the prescription for pharmacist verification.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Patient name</Label><Input data-testid="rx-patient-input" value={form.patient_name} onChange={(e) => setForm((f) => ({ ...f, patient_name: e.target.value }))} /></div>
            <div><Label>Prescription image / PDF</Label><input ref={fileRef} type="file" accept="image/*,.pdf" data-testid="rx-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm mt-1" /></div>
            <div><Label>Prescribing facility</Label><Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="submit-rx-btn" onClick={upload} disabled={busy}>{busy ? "Uploading…" : "Upload"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review & verify dialog */}
      <Dialog open={!!review} onOpenChange={(o) => !o && closeReview()}>
        <DialogContent className="max-w-none w-screen h-[100dvh] max-h-none rounded-none sm:rounded-none overflow-y-auto p-6 sm:p-8" data-testid="rx-review-fullscreen">
          <DialogHeader><DialogTitle>Review prescription — {review?.patient_name || review?.customer_name || ""}</DialogTitle>
            <DialogDescription>Match the prescribed medicines against live stock, attach a customer, then verify or reject.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left: image */}
            <div className="space-y-2">
              {fileKind === "image" && imgUrl ? <img src={imgUrl} alt="prescription" className="w-full rounded-lg border border-border max-h-[520px] object-contain bg-black/5" data-testid="rx-review-image" />
                : fileKind === "pdf" && fileUrl ? <iframe src={fileUrl} title="prescription" className="w-full rounded-lg border border-border h-[520px] bg-white" data-testid="rx-review-pdf" />
                : <div className="rounded-lg border border-border h-64 flex items-center justify-center text-muted-foreground text-sm" data-testid="rx-preview-unavailable">{fileUrl ? "Inline preview not supported — use “Open full file”" : "Preview not available"}</div>}
              {fileUrl && <a href={fileUrl} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1" style={{ color: "var(--mm-action)" }} data-testid="rx-open-file"><ExternalLink className="h-3 w-3" /> Open full file</a>}
            </div>

            {/* Right: matching panel */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Check stock at</Label>
                  <Select value={branchId} onValueChange={(v) => { setBranchId(v); loadStock(v); }}>
                    <SelectTrigger data-testid="rx-branch-select"><SelectValue placeholder="Branch" /></SelectTrigger>
                    <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Customer</Label>
                  <div className="flex gap-1">
                    <Button type="button" size="sm" variant={custMode === "existing" ? "default" : "outline"} className="flex-1" data-testid="rx-cust-existing" onClick={() => setCustMode("existing")}>Existing</Button>
                    <Button type="button" size="sm" variant={custMode === "new" ? "default" : "outline"} className="flex-1" data-testid="rx-cust-new" onClick={() => setCustMode("new")}>New</Button>
                  </div>
                </div>
              </div>
              {custMode === "existing" ? (
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger data-testid="rx-customer-select"><SelectValue placeholder="Select customer (optional)" /></SelectTrigger>
                  <SelectContent className="max-h-64">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.type}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <Input data-testid="rx-newcust-name" placeholder="Name" value={newCust.name} onChange={(e) => setNewCust((c) => ({ ...c, name: e.target.value }))} />
                    <Input data-testid="rx-newcust-phone" placeholder="Phone" value={newCust.phone} onChange={(e) => setNewCust((c) => ({ ...c, phone: e.target.value }))} />
                    <Select value={newCust.type} onValueChange={(v) => setNewCust((c) => ({ ...c, type: v }))}>
                      <SelectTrigger data-testid="rx-newcust-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="insurance">Insurance</SelectItem><SelectItem value="corporate">Corporate</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {newCust.type === "insurance" && (
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={newCust.insurance_scheme_id} onValueChange={(v) => setNewCust((c) => ({ ...c, insurance_scheme_id: v }))}>
                        <SelectTrigger data-testid="rx-newcust-scheme"><SelectValue placeholder="Insurance scheme" /></SelectTrigger>
                        <SelectContent>{schemes.filter((s) => s.is_active !== false).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input data-testid="rx-newcust-memberno" placeholder="Insurance / member no." value={newCust.scheme_member_no} onChange={(e) => setNewCust((c) => ({ ...c, scheme_member_no: e.target.value }))} />
                    </div>
                  )}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Medications</Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" data-testid="rx-missed-sale-btn" onClick={() => setMissedOpen(true)}>Record missed sale</Button>
                    <Button type="button" size="sm" variant="outline" data-testid="rx-add-item" onClick={() => setItems((a) => [...a, { product_id: "", quantity: 1 }])}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {items.map((it, idx) => {
                    const avail = availFor(it.product_id);
                    const qty = Number(it.quantity || 0);
                    const badge = !it.product_id ? null : avail === 0
                      ? <span className="text-rose-600 font-medium">Out of stock</span>
                      : avail < qty ? <span className="text-amber-600 font-medium">Only {avail} left</span>
                      : <span className="text-emerald-600">In stock ({avail})</span>;
                    return (
                      <div key={idx} className={`space-y-1 ${it.rejected ? "rounded-md bg-rose-500/5 p-1.5" : ""}`} data-testid={`rx-item-row-${idx}`}>
                      <div className={`flex items-center gap-2 ${it.rejected ? "opacity-60" : ""}`}>
                        <ProductCombobox products={products} value={it.product_id} onChange={(v) => setItem(idx, { product_id: v })} testId={`rx-item-product-${idx}`} className="flex-1" />
                        <Input type="number" min="1" className="w-16 h-9" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} data-testid={`rx-item-qty-${idx}`} />
                        <span className="text-xs w-24 text-right" data-testid={`rx-item-stock-${idx}`}>{it.rejected ? <span className="text-rose-600 font-medium">Rejected</span> : badge}</span>
                        <Button type="button" size="sm" variant="ghost" className={it.rejected ? "text-emerald-700" : "text-rose-600"} onClick={() => setItem(idx, { rejected: !it.rejected, reject_reason: it.rejected ? "" : it.reject_reason || "" })} data-testid={`rx-item-reject-${idx}`}>{it.rejected ? "Undo" : "Reject line"}</Button>
                        <button className="text-muted-foreground hover:text-rose-600" data-testid={`rx-item-remove-${idx}`} onClick={() => setItems((a) => a.filter((_, i) => i !== idx))}><Trash2 className="h-4 w-4" /></button>
                      </div>
                      {it.rejected && (
                        <div className="flex gap-2">
                          <Select value={LINE_REASONS.includes(it.reject_reason) ? it.reject_reason : it.reject_reason || it.otherReason ? "Other" : ""}
                            onValueChange={(v) => setItem(idx, v === "Other" ? { reject_reason: "", otherReason: true } : { reject_reason: v, otherReason: false })}>
                            <SelectTrigger className="h-8 text-xs flex-1" data-testid={`rx-item-reject-reason-${idx}`}><SelectValue placeholder="Reason for rejecting this line (required)" /></SelectTrigger>
                            <SelectContent>{[...LINE_REASONS, "Other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                          </Select>
                          {(it.otherReason || (it.reject_reason && !LINE_REASONS.includes(it.reject_reason))) && (
                            <Input className="h-8 text-xs flex-1" placeholder="Describe the reason" value={it.reject_reason || ""} onChange={(e) => setItem(idx, { reject_reason: e.target.value, otherReason: true })} data-testid={`rx-item-reject-other-${idx}`} />
                          )}
                        </div>
                      )}
                      </div>
                    );
                  })}
                  {items.length === 0 && <div className="text-xs text-muted-foreground">No medications added yet.</div>}
                </div>
                {anyStockIssue && <p className="text-xs text-amber-600 mt-2">Some items are out of stock or short — you can still verify, reject with reason “Out of stock”, or record a missed sale.</p>}
              </div>
              <MissedSaleDialog open={missedOpen} onOpenChange={setMissedOpen} branchId={branchId} source="prescription" prescriptionId={review?.id} />

              <RxDeliveryFields form={dlv} setForm={setDlv} />

              <div className="grid grid-cols-2 gap-3">
                <div><Label>Months authorised</Label><Input type="number" min="1" data-testid="rx-refill-months" value={refillMonths} onChange={(e) => setRefillMonths(e.target.value)} /></div>
                <div><Label>Days per supply</Label><Input type="number" min="1" data-testid="rx-refill-days" value={refillDays} onChange={(e) => setRefillDays(e.target.value)} /></div>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">Chronic scripts: e.g. 3 months at 30 days each = 3 monthly refills. Each “Send to POS” uses one refill.</p>

              {rejecting && (
                <div className="rounded-lg border border-rose-300 p-3 space-y-2">
                  <Label className="text-rose-600">Rejection reason</Label>
                  <Select value={rejectReason} onValueChange={setRejectReason}>
                    <SelectTrigger data-testid="rx-reject-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                    <SelectContent>{REJECT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input placeholder="Note (optional)" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} data-testid="rx-reject-note" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            {!rejecting ? (
              <>
                <Button variant="outline" className="text-rose-600 border-rose-300" data-testid="rx-reject-toggle" onClick={() => setRejecting(true)}><XCircle className="h-4 w-4 mr-1.5" /> Reject</Button>
                <Button data-testid="rx-verify-btn" onClick={doVerify} disabled={saving}><CheckCircle2 className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Verify & save"}</Button>
              </>
            ) : (
              <>
                <Button variant="outline" data-testid="rx-reject-cancel" onClick={() => setRejecting(false)}>Back</Button>
                <Button className="bg-rose-600 hover:bg-rose-700" data-testid="rx-reject-confirm" onClick={doReject} disabled={saving}>Confirm rejection</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
